import type { NotificationItem, NotificationKind } from '@eriko/shared-types';
import { formatRelativeTime } from '@eriko/shared-types';
import { AlertTriangle, Bell, ClipboardCheck, MessageSquare } from 'lucide-react';
import { cn } from '../lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';

/**
 * Presentational in-app notification bell (backlog C2). Data-agnostic: the host
 * app provides the role-scoped `items` + `unreadCount` (from `GET /api/notifications`)
 * and an `onSelect` that navigates to `item.href`. Used by both the SME dashboard
 * header and the admin sidebar — placement differs via `side` / `align` / `triggerClassName`.
 */

const KIND_ICON: Record<NotificationKind, typeof MessageSquare> = {
  conversation_reply: MessageSquare,
  escalation: AlertTriangle,
  close_ready: ClipboardCheck,
};

export interface NotificationBellProps {
  items: NotificationItem[];
  unreadCount: number;
  onSelect: (item: NotificationItem) => void;
  isLoading?: boolean;
  /** Popover edge alignment (default 'end'). */
  align?: 'start' | 'center' | 'end';
  /** Popover side (default 'bottom'). Admin sidebar uses 'right'. */
  side?: 'top' | 'right' | 'bottom' | 'left';
  /** Override the trigger button styling to match the host chrome. */
  triggerClassName?: string;
}

export function NotificationBell({
  items,
  unreadCount,
  onSelect,
  isLoading = false,
  align = 'end',
  side = 'bottom',
  triggerClassName,
}: NotificationBellProps) {
  const hasUnread = unreadCount > 0;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={
            hasUnread ? `Notifications, ${unreadCount} unread` : 'Notifications'
          }
          className={cn(
            'relative flex items-center justify-center rounded-full text-surface-600 transition-colors hover:text-surface-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-surface-300 cursor-pointer',
            'w-9 h-9',
            triggerClassName,
          )}
        >
          <Bell className="w-[18px] h-[18px]" strokeWidth={1.8} />
          {hasUnread && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-neg text-white text-[10px] font-semibold leading-4 text-center">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align={align}
        side={side}
        sideOffset={8}
        className="w-80 p-0"
      >
        <div className="flex items-center justify-between px-3 py-2.5 border-b border-surface-200">
          <span className="text-sm font-semibold text-surface-900">Notifications</span>
          {hasUnread && (
            <span className="text-[11px] font-medium text-surface-500">{unreadCount} new</span>
          )}
        </div>

        <div className="max-h-80 overflow-y-auto">
          {isLoading ? (
            <p className="px-3 py-6 text-center text-[13px] text-surface-400">Loading…</p>
          ) : items.length === 0 ? (
            <p className="px-3 py-8 text-center text-[13px] text-surface-400">You're all caught up.</p>
          ) : (
            <ul className="divide-y divide-surface-100">
              {items.map((item) => {
                const Icon = KIND_ICON[item.kind] ?? Bell;
                return (
                  <li key={item.id}>
                    <button
                      type="button"
                      onClick={() => onSelect(item)}
                      className="flex w-full items-start gap-2.5 px-3 py-2.5 text-left hover:bg-surface-50 transition-colors cursor-pointer"
                    >
                      <Icon
                        className="w-4 h-4 mt-0.5 shrink-0 text-surface-400"
                        strokeWidth={1.8}
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block text-[13px] font-medium text-surface-900 truncate">
                          {item.title}
                        </span>
                        <span className="block text-[11px] text-surface-400">
                          {formatRelativeTime(item.at)}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
